import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'clsx';
import { Layout, Typography } from 'fronton-react';
import { memo } from 'common/hocs/memo';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils/create-element-key';
import { analyticsUtils, ClickButtonElementName } from 'utils/analytics-utils';
import { CardLinkWithGAPromoClick } from '../card';
import { SwiperSlide, SwiperWithNavigation } from '../swiper';
import { useOtherDataSelector } from './hooks';
import {
  otherSectionLayout,
  otherSectionLink,
  otherSectionStyle,
  otherSectionTitle,
  swiperStyle,
  swiperSlideStyle,
  swiperSlideStyleB,
  otherSectionLinkWithHover,
  swiperStyleB,
} from './other-section-styles';
import { OtherData } from './other-section-types';
import { CardA } from './card-a';
import { CardB } from './card-b';

type OtherSectionProps = {
  className?: string;
  elementBlock: string;
  landingName: string;
  promotionName: string;
  title?: string;
  variant?: 'A' | 'B';
};

/**
 * Вью секции "Другие услуги"
 */
export const OtherSection = memo(
  ({ className, elementBlock, landingName, promotionName, title, variant = 'A' }: OtherSectionProps) => {
    const { t } = useTranslation();
    const otherData = useOtherDataSelector(landingName);
    const refs = useRef<HTMLAnchorElement[]>([]);
    const promotions = useMemo(
      () =>
        otherData.map(({ title: cardTitle }: OtherData, index: number) => ({
          name: promotionName,
          creative: cardTitle,
          position: (index + 1).toString(),
        })),
      [promotionName, otherData]
    );

    usePromoViewEvent(refs, elementBlock, promotions);

    /**
     * Обработчик нажатия кнопки "Следующий слайд"
     */
    const handleNextSlideBtnClick = () => {
      analyticsUtils.clickButtonEvent(elementBlock, ClickButtonElementName.SwitchForward, 'Другие услуги');
    };

    /**
     * Обработчик нажатия кнопки "Предыдущий слайд"
     */
    const handlePrevSlideBtnClick = () => {
      analyticsUtils.clickButtonEvent(elementBlock, ClickButtonElementName.SwitchBackward, 'Другие услуги');
    };

    return (
      <section className={cn(otherSectionStyle, className)}>
        <Layout className={otherSectionLayout}>
          <Typography className={otherSectionTitle} size="extra" variant="h2">
            {title || t('other-section-title', { ns: 'landings-info', defaultValue: 'Другие услуги' })}
          </Typography>
          <SwiperWithNavigation
            className={cn(swiperStyle, { [swiperStyleB]: variant !== 'A' })}
            onSlideNextTransitionEnd={handleNextSlideBtnClick}
            onSlidePrevTransitionEnd={handlePrevSlideBtnClick}
            slidesPerView="auto"
            watchOverflow>
            {otherData.map((info, index) => (
              <SwiperSlide
                key={createElementKey('other', index)}
                className={cn({
                  [swiperSlideStyle]: variant === 'A',
                  [swiperSlideStyleB]: variant === 'B',
                })}
              >
                <CardLinkWithGAPromoClick
                  bannerRefs={refs}
                  elementBlock={elementBlock}
                  className={cn(
                    otherSectionLink,
                    { [otherSectionLinkWithHover]: variant === 'A' }
                  )}
                  data-testid="other-banner"
                  href={info.url}
                  position={index}
                  promotion={promotions[index]}
                  title={info.title}
                >
                  {variant === 'A' ? (
                    <CardA {...info} />
                  ) : (
                    <CardB {...info} />
                  )}
                </CardLinkWithGAPromoClick>
              </SwiperSlide>
            ))}
          </SwiperWithNavigation>
        </Layout>
      </section>
    );
  }
);
